import { httpRequest } from '@/utils/umiRequest';
import { formatTableParams, formatTableResponse, isPro, isStaging } from '@/utils/utils';
import { BASEURL } from '@/utils/enums';
import { getGlobalConfigByModule } from '../configs';

export interface saasDisputeRequest {
  currentPage: number;
  lists: Record<string, any>[];
  pageSize: number;
  total: number;
  totalPage: number;
}

export interface Datum {
  canned_response: CannedResponse[];
  created_at: string;
  freshdesk_id: number;
  id: number;
  name: string;
  personal: number;
  responses_count: number;
  type: number;
  updated_at: string;
  [property: string]: any;
}

export interface CannedResponse {
  amount: string;
  attachments: null;
  content_html: string;
  created_at: string;
  created_user_id: number;
  currency: string;
  folder_id: number;
  folder_type: number;
  freshdesk_id: number;
  group_ids: null;
  id: number;
  negative_title: string;
  positive_title: string;
  scope: number;
  title: string;
  type: string;
  updated_at: string;
  visibility: number;
  [property: string]: any;
}

/** 获取分组-new */
export async function getAllSessionsTable(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<saasDisputeRequest>('/chat-ticket', params, options);
}

//获取分组回复
export async function getFolderResponse(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.get<Datum[]>('/canned-response/folder-responses', params, options);
}

//获取分组回复
export async function getFolderResponseCancelable(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return httpRequest.cancelable.get('/canned-response/folder-responses', params, options);
}

//预设话术总数修改
export async function setUseTotal(params: Record<string, any>, options?: Record<string, any>) {
  return httpRequest.cancelable.post('/canned-response/use-total', params, options);
}

// 获取大数据订单详情
export function getOrderDetail(orderId: string, options?: Record<string, any>) {
  return httpRequest.get<any>(`/lfd/ppquery/order/${orderId}`, options);
}

// 获取大数据订单详情（可取消）
export function getOrderDetailCancelable(orderId: string, options?: Record<string, any>) {
  return httpRequest.cancelable.get<any>(`/lfd/ppquery/order/${orderId}`, options);
}

// 争议工单详情
export async function getSaasDisputeDetail(id: string, options?: Record<string, any>) {
  return await httpRequest.get<any>(`/chat-ticket/${id}`, {}, options);
}

// 被回复消息摘要快照（查询时刻快照，不随后续编辑/撤回回写）
// NOTE id 用 string：雪花长整型以 number 承载会超 2^53 丢精度（AGENTS.md 约定 ID 统一 string）
export interface ChatReplyMessageSnapshot {
  id: string;
  /** 发送者身份：0 访客/客户 / 1 客服（访客侧客服昵称由后端脱敏） */
  type: number;
  sender_id: number | null;
  sender_name: string;
  message_content: string;
  /** 1=被引用消息已撤回（正文为空串，引用不回收） */
  is_recalled: number;
}

// 聊天历史消息项
export interface ChatMessageItem {
  id: number;
  message_status: number;
  chat_ticket_id: number;
  message_content: string;
  sender_name: string;
  sender_id: number | null;
  type: number;
  /** 0-正常 1-已撤回（撤回后 message_content 为空串） */
  is_recalled: number;
  /** 撤回时间 */
  recalled_at: string | null;
  /** 0-未编辑 1-已编辑 */
  is_edited: number;
  /** 最后编辑时间 */
  edited_at: string | null;
  /** 被回复消息 id（雪花 id 数字字符串）；0/缺省 = 非回复消息 */
  reply_to_id?: string;
  /** 被回复消息摘要快照；非回复消息为 null */
  reply_message?: ChatReplyMessageSnapshot | null;
  created_at: string;
  updated_at: string;
}

// 聊天历史消息（游标分页）
export interface ChatMessageData {
  lists: ChatMessageItem[];
  next_cursor: number | null;
  has_more: boolean;
  /** 访客（客户）已读边界：id 不大于该值的消息客户已读 */
  visitor_last_read_message_id?: number | string;
}

// 获取聊天消息（不传参数获取最新一页，传 next_cursor 加载更早的历史消息）
// NOTE 该接口走 /api/chat 前缀，需覆盖全局默认的 /api/admin baseURL
export async function getChatDetail(
  id: string,
  params?: { cursor?: number | null },
  options?: Record<string, any>,
) {
  return await httpRequest.get<ChatMessageData>(
    `/chat-ticket/${id}/messages`,
    params ?? { },
    options,
  );
}

// Centrifugo token 载荷：connection_token 为连接 JWT，
// subscriptions 为 频道名 -> 该频道的订阅 JWT（如 chat:77 工单频道）
export interface CentrifugoTokenData {
  connection_token: string;
  subscriptions: Record<string, string>;
}

// Centrifugo token（GET /chat-ticket/{id}/centrifugo-token）
// NOTE 该接口走 /api/admin 前缀；返回结构可能是 { data: {...} } 包裹，也可能直接平铺在顶层，
// 类型上两种都兼容，由调用处归一化
export interface CentrifugoTokenResponse extends CentrifugoTokenData {
  data?: CentrifugoTokenData;
}

// 获取 Centrifugo 连接 token 与各频道订阅 token
export async function getCentrifugoToken(id: string, options?: Record<string, any>) {
  return await httpRequest.get<CentrifugoTokenResponse>(
    `/chat-ticket/${id}/centrifugo-token`,
    {},
    options,
  );
}

// 租户级客服频道 token（GET /chat-ticket/agent-centrifugo-token），用于列表页实时推送
export interface AgentCentrifugoTokenData {
  connection_token: string;
  /** 服务端签发的频道名（如 chat:agent:t:2），前端不自行拼接 */
  channels: string[];
  subscriptions: Record<string, string>;
  ttl?: number;
}

// 获取列表页租户频道连接 token
// NOTE 返回结构可能是 { data: {...} } 包裹，也可能直接平铺在顶层，由调用处归一化
export async function getAgentCentrifugoToken(options?: Record<string, any>) {
  return await httpRequest.get<AgentCentrifugoTokenData & { data?: AgentCentrifugoTokenData }>(
    '/chat-ticket/agent-centrifugo-token',
    {},
    options,
  );
}

// 客服标记已读返回数据（unread_count/unread_total 为上报后的最新未读）
export interface AgentReadResultData {
  chat_ticket_id: string;
  last_read_message_id: string;
  unread_count: number;
  unread_total: number;
}

// 客服标记工单已读（POST /chat-ticket/{id}/read）
// NOTE last_message_id 不传时后端推进到本单 MAX(id)，规避在途请求期间新消息到达的漏报
export async function markChatTicketRead(
  id: string,
  lastMessageId?: string,
  options?: Record<string, any>,
) {
  return await httpRequest.post<AgentReadResultData>(
    `/chat-ticket/${id}/read`,
    lastMessageId !== undefined ? { last_message_id: lastMessageId } : undefined,
    options,
  );
}

// 发送消息入参（type 为发送者身份：0 客户 / 1 客服）
export interface SendChatMessageParams {
  type: number;
  sender_name: string;
  content: string;
  /**
   * 被回复消息 id（引用回复）；不传 = 非回复消息（后端视为 0）
   * NOTE 传数字字符串规避雪花大整数转 JS number 丢精度，后端 integer 规则自行强转
   */
  reply_to_id?: string | number;
}

// 发送消息（返回新创建的消息）
// NOTE 该接口走 /api/chat 前缀，需覆盖全局默认的 /api/admin baseURL
export async function sendChatMessage(
  id: string,
  params: SendChatMessageParams,
  options?: Record<string, any>,
) {
  return await httpRequest.post<ChatMessageItem>(
    `/chat-ticket/${id}/messages`,
    params,
    options,
  );
}

// 创建邀请链接返回数据
export interface ChatInvitationData {
  id: string;
  // 32位随机邀请码
  code: string;
  // CHAT_INVITATION_URL 模板渲染，未配置则为 null
  url: string | null;
  tenant_id: string;
  inviter_id: string;
  web_url: string;
  // 过期日期（Y-m-d），null = 永久有效；创建后不可修改（延期 = 重新创建）
  expires_at: string | null;
  created_at: string;
}

// 创建邀请链接入参（v1.12.0）
export interface CreateChatInvitationParams {
  // 过期日期 Y-m-d，可选；不传 = 永久有效
  expires_at?: string;
}

// 创建邀请链接（POST /api/admin/chat-ticket/invitation）
export async function createChatInvitation(
  params?: CreateChatInvitationParams,
  options?: Record<string, any>,
) {
  return await httpRequest.post<ChatInvitationData>('/chat-ticket/invitation', params ?? {}, options);
}

// 会话列表项（工单侧栏会话列表用）
export interface ChatConversationItem {
  id: number;
  status: number;
  contact_name: string;
  unread_count: number;
  /** 最新一条消息；工单尚无消息时为 null */
  last_message: {
    sender_name: string;
    message_content: string;
    created_at: string;
  } | null;
}

// 会话列表响应：按最新消息时间倒序，无消息工单排最前
export interface ChatConversationData {
  lists: ChatConversationItem[];
  total: number;
  page: number;
  per_page: number;
}

// 获取工单会话列表（GET /api/admin/chat-ticket/conversations），滚动到底部按 page 递增翻页
export async function getChatConversations(
  params?: { page?: number; per_page?: number },
  options?: Record<string, any>,
) {
  return await httpRequest.get<ChatConversationData>(
    '/chat-ticket/conversations',
    params ?? { },
    options,
  );
}

// 撤回/编辑消息响应：经 umi 请求层解构后直接是操作后的完整消息对象，
// 与 getChatDetail/sendChatMessage 同口径；业务失败（403/404/400）已在拦截器统一抛错

// 编辑消息入参：content 为新正文（非空，最长 5000 字符，服务端会做 HTML 消毒）
export interface UpdateChatMessageParams {
  content: string;
}

// 撤回消息（POST /chat-ticket/{ticket}/messages/{message}/recall）
// 仅限本人发送的客服消息，发送后 5 分钟内可操作；撤回后正文清空且不可恢复
export async function recallChatMessage(
  ticketId: string | number,
  messageId: string | number,
  options?: Record<string, any>,
) {
  return await httpRequest.post<ChatMessageItem>(
    `/chat-ticket/${ticketId}/messages/${messageId}/recall`,
    {},
    options,
  );
}

// 编辑消息（PUT /chat-ticket/{ticket}/messages/{message}）
// 仅限本人发送的客服消息，发送后 5 分钟内可操作；已撤回的消息不可编辑
export async function updateChatMessage(
  ticketId: string | number,
  messageId: string | number,
  params: UpdateChatMessageParams,
  options?: Record<string, any>,
) {
  return await httpRequest.put<ChatMessageItem>(
    `/chat-ticket/${ticketId}/messages/${messageId}`,
    params,
    options,
  );
}

// 获取物流信息
export async function getTrackExpressRecord(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>('/lfd/track/express/record', params, options);
}

// 物流虚拟单号查询
export async function getVmExpressRecord(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.get<any>(`/lfd/track/vm-express/record`, params, options);
}

// 新增争议工单标签
export async function createSaasDisputeTag(
  id: string,
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/saas-dispute/${id}/tags`, params, options);
}

// 设置争议工单标签
export async function setSaasDisputeTag(
  id: string,
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.put<any>(`/saas-dispute/${id}/tags`, params, options);
}

// 移除争议工单标签
export async function removeSaasDisputeTag(
  id: string,
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.delete<any>(`/saas-dispute/${id}/tags`, params, options);
}

// 获取标签
export async function getSaasDisputeTag(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.get<any>(`/tags`, params, options);
}

// 获取咨询原因
export async function getDisputeReason(options?: Record<string, any>) {
  return await httpRequest.get<any>(`/dispute-reason`, {}, options);
}

// 设置备注
export async function setRemarks(
  id: string,
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.put<any>(`/chat-ticket/${id}/remark`, params, options);
}

// 修改订单号
export async function updateOrderNumber(
  id: string,
  params: { order_id: string },
  options?: Record<string, any>,
) {
  return await httpRequest.put<any>(`/saas-dispute/${id}/orders`, params, options);
}

// 获取智能推荐列表数据
export async function getIntelligentReplyListData(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/chat/qdesksaas/recommendations`, params, {
    ...options,
    headers: {
      isRecommendations: true,
    },
  });
}

// 采纳计数
export async function updateAdoptionCount(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/chat/qdesksaas/adoption`, params, options);
}

// 上传采纳信息
export async function updateAdoptionInfo(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/chat/qdesksaas/adoption-record/store`, params, options);
}

// 新增推荐
export async function createRecommendations(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/chat/qdesksaas/recommendations/store`, params, options);
}

// 变更采纳状态
export async function changeRecommendations(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.put<any>(
    `/saas-dispute/change-adoption/${params.id}`,
    { is_adoption: params.status },
    options,
  );
}

// 获取采纳状态
export async function getRecommendationStatus(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.get<any>(`/saas-dispute/adoption-status/${params.id}`, {}, options);
}

export async function toTranslate(message: any[], target_lang?: string, source?: number) {
  return await httpRequest.post<any>(
    `/util/translate`,
    {
      target_language: target_lang,
      text: message,
      source,
    },
    {},
  );
}

// 历史消息翻译结果保存
export async function saveSaasTranslateMessage(
  message_id: string,
  data: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/saas-dispute/message/${message_id}`, data, options);
}

// 历史消息翻译结果保存
export async function saveEmailTranslateMessage(
  message_id: string,
  data: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/email-ticket/email/${message_id}`, data, options);
}
// 批量关闭
export async function batchCloseDispute(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.put<any>(`/saas-dispute/set-status-close`, params, options);
}

// 工单标题翻译结果保存
export async function saveSubjectTranslateMessage(
  ticket_id: number,
  data: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/email-ticket/translate/${ticket_id}`, data, options);
}

/**
 * 获取SaaS工单模块的配置
 * @param params
 * @param options
 * @param cancelable 可否取消
 * @returns
 */
export async function getSaasDisputeTicketConfig(
  options?: Record<string, any>,
  cancelable = false,
) {
  return await getGlobalConfigByModule('saas-ticket', options, cancelable);
}
